import {
  ResourceAdvancedFilter,
  ResourceFilterOptionList,
  ResourceFilterSelect,
  type ResourceFilterOption
} from '@/shared/ui/Layout'

export type StorePartnerFilter = {
  partnerId: string | null
  categoryId: string | null
}

const EMPTY_PARTNER_FILTER: StorePartnerFilter = {
  partnerId: null,
  categoryId: null
}

const LABELS = {
  title: 'Filtros',
  partner: 'Parceiro',
  category: 'Categoria',
  allPartners: 'Todos os parceiros',
  allCategories: 'Todas as categorias',
  clearAll: 'Limpar filtros',
  apply: 'Aplicar'
} as const

type StoreAdvancedFilterProps = {
  filters: StorePartnerFilter
  partnerOptions: ResourceFilterOption<string>[]
  categoryOptions: ResourceFilterOption<string>[]
  hasActiveFilters: boolean
  onApplyDraft: (draft: StorePartnerFilter) => void
  onReset: () => void
  className?: string
}

const hasAdvancedValue = (filters: StorePartnerFilter) =>
  Boolean(filters.partnerId || filters.categoryId)

export const StoreAdvancedFilter = ({
  filters,
  partnerOptions,
  categoryOptions,
  hasActiveFilters,
  onApplyDraft,
  onReset,
  className
}: StoreAdvancedFilterProps) => (
  <ResourceAdvancedFilter
    title={LABELS.title}
    clearLabel={LABELS.clearAll}
    applyLabel={LABELS.apply}
    appliedFilters={filters}
    emptyDraft={EMPTY_PARTNER_FILTER}
    hasActiveFilters={hasActiveFilters}
    isDraftActive={hasAdvancedValue}
    hidden={partnerOptions.length === 0 && categoryOptions.length === 0}
    className={className}
    triggerAriaLabel={LABELS.title}
    onReset={onReset}
    onApplyDraft={onApplyDraft}
    renderFields={({ layout, filters: currentFilters, updateFilters }) => {
      const partnerField =
        partnerOptions.length > 0
          ? layout === 'mobile'
            ? (
                <ResourceFilterOptionList
                  label={LABELS.partner}
                  emptyLabel={LABELS.allPartners}
                  options={partnerOptions}
                  value={currentFilters.partnerId}
                  onChange={partnerId => updateFilters({ partnerId })}
                />
              )
            : (
                <ResourceFilterSelect
                  id="store-filter-partner"
                  label={LABELS.partner}
                  emptyLabel={LABELS.allPartners}
                  options={partnerOptions}
                  value={currentFilters.partnerId}
                  onChange={partnerId => updateFilters({ partnerId })}
                />
              )
          : null

      const categoryField =
        categoryOptions.length > 0
          ? layout === 'mobile'
            ? (
                <ResourceFilterOptionList
                  label={LABELS.category}
                  emptyLabel={LABELS.allCategories}
                  options={categoryOptions}
                  value={currentFilters.categoryId}
                  onChange={categoryId => updateFilters({ categoryId })}
                />
              )
            : (
                <ResourceFilterSelect
                  id="store-filter-category"
                  label={LABELS.category}
                  emptyLabel={LABELS.allCategories}
                  options={categoryOptions}
                  value={currentFilters.categoryId}
                  onChange={categoryId => updateFilters({ categoryId })}
                />
              )
          : null

      if (!partnerField && !categoryField) return null

      return (
        <div className="flex flex-col gap-4">
          {partnerField}
          {categoryField}
        </div>
      )
    }}
  />
)

StoreAdvancedFilter.displayName = 'StoreAdvancedFilter'
